import { useCallback, useMemo, useState } from "react";
import { useFeedInput } from "@/features/study-group-stream/contexts/FeedInputContext";
import {
  INITIAL_REPLIES_VISIBLE,
  INITIAL_VISIBLE,
  resolveReplyParentId,
  shouldPostAsReply,
} from "@/features/study-group-stream/utils/commentSectionUtils";
import { sortTopLevelCommentsDesc } from "@/features/study-group-stream/utils/streamUtil";
import { usePostComments } from "@/hooks/usePostComments";
import type { ReactionType } from "@/lib/humhub-api/types";

export interface UseCommentSectionProps {
  postContentId: number;
  postObjectId: number;
  studyGroupId?: number | null;
  selectedTopics?: Array<{
    id?: number | string;
    name: string;
    topic_kind?: string;
  }>;
  currentUser?: {
    id: number;
    guid?: string;
    display_name: string;
    url?: string;
    image_url?: string;
  } | null;
  onCommentSubmitted?: (
    result: SubmittedCommentResult,
    context: {
      objectId: string | number;
      objectModel?: "comment" | "post";
    },
  ) => void;
}

export type SubmittedCommentResult = {
  createdCommentId: number | null;
  parentCommentId: number | null;
};

export function useCommentSection({
  postContentId,
  postObjectId,
  studyGroupId,
  selectedTopics,
  currentUser,
  onCommentSubmitted,
}: UseCommentSectionProps) {
  const {
    comments,
    total,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    addComment,
    addReply,
    editComment,
    deleteComment,
    reactComment,
  } = usePostComments({
    postContentId,
    postObjectId,
    groupId: studyGroupId ?? undefined,
    selectedTopics,
    currentUser,
  });

  // ===== UI State =====
  // コメント入力（返信）のコンテキスト
  const { activeInput, closeAll, toggleReply } = useFeedInput();
  const isReplyForPost =
    activeInput?.type === "reply" && activeInput.postId === postObjectId;
  const replyCtx = isReplyForPost
    ? { parentId: activeInput.parentId, anchorId: activeInput.anchorId }
    : null;
  // 返信が展開されているコメントIDのセット
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(
    new Set(),
  );
  // トップレベルコメントが展開されているか
  const [isExpanded, setIsExpanded] = useState(false);

  // ===== Memoized Derived State =====
  const sortedComments = useMemo(
    () => sortTopLevelCommentsDesc(comments),
    [comments],
  );

  const visibleComments = useMemo(
    () =>
      isExpanded ? sortedComments : sortedComments.slice(0, INITIAL_VISIBLE),
    [isExpanded, sortedComments],
  );

  const hasMoreComments = isExpanded ? !!hasNextPage : total > INITIAL_VISIBLE;

  // 各コメントに表示状態を追加
  const commentsWithVisibility = useMemo(() => {
    return visibleComments.map((comment) => {
      const allReplies = sortTopLevelCommentsDesc(comment.comments || []);
      const areRepliesExpanded = expandedReplies.has(comment.id);
      const visibleReplies = areRepliesExpanded
        ? allReplies
        : allReplies.slice(0, INITIAL_REPLIES_VISIBLE);
      const hiddenRepliesCount = Math.max(
        0,
        allReplies.length - visibleReplies.length,
      );

      return {
        ...comment,
        visibleReplies,
        hiddenRepliesCount,
        areRepliesExpanded,
      };
    });
  }, [visibleComments, expandedReplies]);

  // ===== Event Handlers =====
  const handleLoadMoreComments = useCallback(async () => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
    if (hasNextPage) {
      await fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isExpanded]);

  const handleCollapseComments = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const expandAllComments = useCallback(() => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  }, [isExpanded]);

  const expandRepliesForComment = useCallback((commentId: number) => {
    setExpandedReplies((prev) => {
      if (prev.has(commentId)) return prev;
      const next = new Set(prev);
      next.add(commentId);
      return next;
    });
  }, []);

  const handleToggleReplies = (commentId: number) => {
    setExpandedReplies((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) {
        next.delete(commentId);
      } else {
        next.add(commentId);
      }
      return next;
    });
  };

  const handleReplyClick = (parentId: number, anchorId: number) => {
    toggleReply(postObjectId, parentId, anchorId);
  };

  const handleClearReplyCtx = useCallback(() => {
    if (isReplyForPost) {
      closeAll();
    }
  }, [isReplyForPost, closeAll]);

  const handleSubmitComment = async (
    content: string,
    objectId: string | number,
    objectModel?: "comment" | "post",
    files?: File[],
  ): Promise<SubmittedCommentResult> => {
    if (!shouldPostAsReply(replyCtx, objectModel)) {
      const createdComment = await addComment.mutateAsync({
        content,
        files,
        topics: selectedTopics,
      });
      const result = {
        createdCommentId:
          typeof createdComment.id === "number" ? createdComment.id : null,
        parentCommentId: null,
      };
      onCommentSubmitted?.(result, { objectId, objectModel });
      return result;
    }

    const parentId = resolveReplyParentId(objectId, replyCtx);
    if (!parentId) {
      console.error("Missing parent comment ID for reply submission");
      const result = {
        createdCommentId: null,
        parentCommentId: null,
      };
      onCommentSubmitted?.(result, { objectId, objectModel });
      return result;
    }

    const createdReply = await addReply.mutateAsync({
      parentCommentId: parentId,
      content,
      files,
      topics: selectedTopics,
    });
    expandRepliesForComment(parentId);
    closeAll();
    const result = {
      createdCommentId:
        typeof createdReply.id === "number" ? createdReply.id : null,
      parentCommentId: parentId,
    };
    onCommentSubmitted?.(result, { objectId, objectModel });
    return result;
  };

  const handleDeleteComment = async (commentId: number) => {
    // UIからの確認は呼び出し側で行う
    await deleteComment.mutateAsync({ commentId });
  };

  const handleEditComment = async (commentId: number, newMessage: string) => {
    await editComment.mutateAsync({ commentId, newMessage });
  };

  const handleReactComment = async (
    commentId: number,
    reaction: ReactionType | null,
  ) => {
    await reactComment.mutateAsync({ commentId, reaction });
  };

  return {
    // --- State & Data ---
    comments: commentsWithVisibility,
    allComments: sortedComments,
    totalComments: total,
    hasMoreComments,
    isLoadingMoreComments: isFetchingNextPage,
    replyCtx,
    currentUser,
    studyGroupId,
    postObjectId,
    isExpanded,

    // --- Mutations & Status ---
    isSubmitting: addComment.isPending || addReply.isPending,
    isEditing: editComment.isPending,
    isDeleting: deleteComment.isPending,
    isReacting: reactComment.isPending,

    // --- Handlers ---
    handleLoadMoreComments,
    handleCollapseComments,
    expandAllComments,
    expandRepliesForComment,
    handleToggleReplies,
    handleReplyClick,
    handleClearReplyCtx,
    handleSubmitComment,
    handleDeleteComment,
    handleEditComment,
    handleReactComment,
  };
}
